import type { Attachment } from './project'
import type { PreUser, User } from './user'

// Snapshot data (immutable historical info)
export interface PartySnapshot {
  first_name: string
  last_name: string
  email: string
  phone?: string | null
  company_name?: string | null
  company_address?: string | null
}

export enum PayRateTypes {
  HOURLY = 'HOURLY',
  PROJECT = 'PROJECT',
}

export type TagStatus =
  | 'DRAFT'
  | 'NEW'
  | 'ACCEPTED'
  | 'IN_PROGRESS'
  | 'COMPLETE'
  | 'SIGNED'
  | 'DECLINED'

export enum TagStatusEnum {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  SIGNED = 'SIGNED',
  DECLINED = 'DECLINED',
}

export const formatTagStatus = (status: TagStatus): string => {
  switch (status.toUpperCase()) {
    case 'NEW':
      return 'New'
    case 'ACCEPTED':
      return 'Accepted'
    case 'IN_PROGRESS':
      return 'In progress'
    case 'COMPLETE':
      return 'Complete'
    case 'SIGNED':
      return 'Signed at Site'
    default:
      return status
  }
}

export interface Tag {
  id: number
  label: string
  title: string | null
  created_date: string
  updated_date: string
  created_by: {
    id?: number
    first_name: string
    last_name: string
    phone_number: string
  }
  created_by_id?: number
  // Driver-editable fields
  loading_arrive?: string
  loading_depart?: string
  // Decline fields
  decline_reason?: string
  declined_by?: User
  declined_at?: string
  assigned_to: {
    id: number
    added_at: string
    contact_object: {
      id: number
      email: string
      type: string
      name: string
      phone_number?: string
      stakeholder_types: string[]
    }
  }
  assigned_user?: User
  assigned_preuser?: PreUser
  status: TagStatus
  load_info: LoadInfo
  origin_address: string
  origin_name: string
  destination_address: string
  entity: string
  notes: string | null
  supervisor_signature: string | null
  driver_signature: string | null
  pay_rate: string
  rate_type: PayRateTypes
  customer: string
  material: string | null
  additional_expenses: AdditionalExpenses
  // New site supervisor fields
  site_supervisor: User | null
  project_site_supervisors: User[]
  workorder:
    | number
    | {
        id: number
        pay_rate?: string | number
        rate_type?: string
        subhaulers?: Array<{
          id: number
          first_name: string
          last_name: string
          email: string
        }>
      }
  project: number | { id: number }
  broker?: number
  start_date?: string
  // Draft fields
  draft_expires_at?: string
  sent_at?: string
  // Snapshot fields (immutable)
  assigned_driver_first_name?: string
  assigned_driver_last_name?: string
  assigned_driver_email?: string
  assigned_driver_phone?: string
  assigned_driver_company_name?: string
  site_supervisor_first_name?: string
  site_supervisor_last_name?: string
  site_supervisor_email_snapshot?: string
  site_supervisor_phone_snapshot?: string
  created_by_first_name?: string
  created_by_last_name?: string
  created_by_company_name?: string
  // Contractor and broker user info (from project/workorder)
  contractor_user?: {
    id: number
    first_name: string
    last_name: string
    email: string
    phone_number?: string
  }
  broker_user?: {
    id: number
    first_name: string
    last_name: string
    email: string
    phone_number?: string
  }
  // Snapshot data from backend (resolved from project/workorder snapshots - immutable historical data)
  contractor_snapshot?: PartySnapshot
  broker_snapshot?: PartySnapshot
  sub_hauler_snapshot?: PartySnapshot
  dispatcher_snapshot?: PartySnapshot
  // Role of who created/sent the dispatch (BROKER, CONTRACTOR, SUB_HAULER, etc.)
  dispatch_created_by_role?: string
  // Created-by snapshot (inline on Tag)
  created_by_email?: string
  // Attachments
  attachments?: Attachment[]
}

export interface CreateTagData {
  created_date: string
  updated_date: string
  assigned_user_id: number
  assigned_user?: User // Added for performance optimization
  created_by_id: number
  destination_address: string
  entity: string
  origin_address: string
  origin_name: string
  title: string
  customer: string
  rate_type: 'TIME' | 'WEIGHT' | 'LOAD'
  pay_rate: string
  material: string
  load_info: Record<string, unknown>
  notes?: string
  additional_expenses: Record<string, unknown>
  workorder_id?: number
  project_id?: number
  start_date: string
}

export type TagTableProps = {
  tags: Tag[]
}

export interface LoadInfo {
  notes: string
  weight: string
  material: string
  scale_tag_id: string
  tolls: string
}

export interface AdditionalExpenses {
  tolls?: string
  yardTime?: string
  homeTime?: string
  notes?: string
}

export interface TagCardProps {
  tag: Tag
  onFillOut: (tagId: string) => void
}

export interface ApiTag {
  id: number
  label: string
  created_date: string
  status: string
  created_by: {
    username: string
    stakeholder_type: string
    phone_number?: string
    first_name: string
    last_name: string
  }
  assigned_to: {
    username: string
    stakeholder_type: string
    phone_number?: string
    email: string
    first_name: string
    last_name: string
  }
  load_info: LoadInfo
  additional_expenses: AdditionalExpenses
  material: string
  origin_address: string
  origin_name: string
  destination_address: string
  entity: string
  title: string
  notes: string | null
  supervisor_signature: string | null
  driver_signature: string | null
  rate_type: PayRateTypes
  pay_rate: string
  customer: string
  updated_date: string
  site_supervisor: User | null
  project_site_supervisors: User[]
  shared_emails: string[]
  workorder_id: number
  start_date: string
  workorder: {
    id: number
  }
}
